import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Bookmark} from "lucide-react";
import {get, join} from 'lodash';
import React from "react";
import {Link} from "react-router-dom";
import {sessionCompletionPercentage, sessionDurationHumanReadable} from "business/session";
import {convertUrl} from "common/helpers";
import {color, font, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import {useTranslation} from "react-i18next";
import Card from "components/redesign/Card";
import Chip from "components/redesign/Chip";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles({
  link: {
    display: 'block',
    textDecoration: 'none',
    height: '100%',
  },
  card: {
    height: '100%',
  },
  strip: {
    width: 208,
    flexShrink: 0,
  },
  media: {
    position: 'relative',
    background: color.SURFACE_MUTED,
  },
  banner: {
    width: '100%',
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
  },
  // The glyph is white, so it needs a scrim to stay legible on a light banner.
  bookmark: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0, 0, 0, 0.4)',
    color: color.SURFACE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  progressTrack: {
    height: 4,
    background: color.RING_TRACK,
    width: '100%',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    background: color.RING_FILL,
  },
  body: {
    padding: 14,
  },
  bodyBadge: {
    display: 'inline-flex',
    marginBottom: 8,
  },
  title: {
    ...type.sessionTitle,
    color: color.CARD_TITLE,
    margin: 0,
    wordBreak: 'break-word',
  },
  tags: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '4px 0 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  duration: {
    ...type.meta,
    color: color.CARD_BODY,
    margin: '10px 0 0',
  },
});

const HubSessionCard = ({session, toggleBookmark, strip = false}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const banner = get(session, 'banner');
  const tags = join(get(session, 'tags'), ', ');
  const isComplete = session.sessionStatus === 'COMPLETED';
  const progress = isComplete ? 100 : sessionCompletionPercentage(session);
  const statusChip = isComplete ? (
    <Chip variant={'status'} done>{t('sessionTagCompleted')}</Chip>
  ) : progress > 0 ? (
    <Chip variant={'status'}>{t('sessionTagInProgress')}</Chip>
  ) : null;

  const bookmark = event => {
    event.preventDefault();
    toggleBookmark(session.id);
  };

  return (
    <Link
      to={`/session/sessionintroduction/${session.id}`}
      className={classNames(classes.link, strip && classes.strip)}
    >
      <Card variant={'media'} className={classes.card}>
        <div className={classes.media}>
          {Boolean(banner) && (
            <ShimmerImage
              src={convertUrl(banner)}
              fit={'auto'}
              placeholderHeight={116}
              className={classes.banner}
            />
          )}
          {Boolean(banner) && statusChip && (
            <span className={classes.statusBadge}>{statusChip}</span>
          )}
          {Boolean(toggleBookmark) && (
            <button type={'button'} className={classes.bookmark} onClick={bookmark}>
              <Bookmark size={14} fill={session.isBookmarked ? 'currentColor' : 'transparent'}/>
            </button>
          )}
        </div>

        {progress > 0 && (
          <div className={classes.progressTrack}>
            <span className={classes.progressFill} style={{width: `${progress}%`}}/>
          </div>
        )}

        <div className={classes.body}>
          {/* With no banner the badge has nothing to sit on, so it moves into
              the body rather than disappearing. */}
          {!banner && statusChip && <span className={classes.bodyBadge}>{statusChip}</span>}
          <p className={classes.title}>{st(session.title, session.localeTitle)}</p>
          {Boolean(tags) && <p className={classes.tags}>{tags}</p>}
          {/* A completed session already says so on its badge. */}
          {!isComplete && (
            <p className={classes.duration}>{sessionDurationHumanReadable(session)}</p>
          )}
        </div>
      </Card>
    </Link>
  );
};

export default HubSessionCard;
